// Tiny in-memory request cache — avoids refetching the same data on every tab
// switch (less load on the DB/server). Entries expire after ttlMs.
type Entry = { t: number; data: any };
const store = new Map<string, Entry>();

/** Return cached data for `key` if fresh, else run `fetcher`, cache, and return it. */
export function cached<T>(key: string, ttlMs: number, fetcher: () => Promise<T>): Promise<T> {
  const hit = store.get(key);
  if (hit && Date.now() - hit.t < ttlMs) return Promise.resolve(hit.data as T);
  return fetcher().then((data) => {
    store.set(key, { t: Date.now(), data });
    return data;
  });
}

/** Is there a fresh-enough entry? Lets a view skip its loading spinner on a warm cache. */
export function hasCached(key: string, ttlMs = 3600000): boolean {
  const hit = store.get(key);
  return !!hit && Date.now() - hit.t < ttlMs;
}

/** Synchronously read a fresh entry (or undefined). For seeding state on mount. */
export function peek<T>(key: string, ttlMs = 3600000): T | undefined {
  const hit = store.get(key);
  return hit && Date.now() - hit.t < ttlMs ? (hit.data as T) : undefined;
}

/** Store data under a key (refreshes its timestamp). Pairs with peek(). */
export function put(key: string, data: any): void {
  store.set(key, { t: Date.now(), data });
}

/** Drop cache entries. No prefix → clear all; prefix → clear matching keys (after a mutation). */
export function invalidate(prefix?: string): void {
  if (!prefix) { store.clear(); return; }
  for (const k of Array.from(store.keys())) if (k.startsWith(prefix)) store.delete(k);
}
